import { ExternalLink, Pencil, Star, Trash2, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { getTradeById, updateTrade, useQuery } from "wasp/client/operations";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import { Button } from "../../client/components/ui/button";
import { useTradeEditing } from "../../trading-journal/useTradeEditing";
import { useLiteData } from "../data/LiteDataProvider";
import { net } from "../lib/stats";
import { SideTag } from "./TradeList";
import { FirmMark, Pnl, labelCls } from "./ui";
import { useHideSupportLauncher } from "./useHideSupportLauncher";

/** Panneau latéral de détail d'un trade. Les modifications passent par l'action updateTrade réelle. */
export function TradeDrawer({ tradeId, onClose }: { tradeId: string | null; onClose: () => void }) {
  useHideSupportLauncher(tradeId !== null);
  const t = useTranslation().lite;
  const { toast } = useToast();
  const { trades, accountsById, strategies, tags, fmt, refetchAll } = useLiteData();
  const trade = trades.find(x => x.id === tradeId);
  // Modifier / supprimer : mêmes fenêtres et mêmes contrôles que le journal Advanced.
  const editing = useTradeEditing({ onChanged: refetchAll });
  // Trade complet (champs personnalisés compris) pour pré-remplir le formulaire de modification.
  const { data: fullTrade } = useQuery(getTradeById, { id: tradeId ?? "" }, { enabled: !!tradeId });
  const [note, setNote] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [rating, setRating] = useState<number | null>(null);

  useEffect(() => {
    setNote(trade?.note ?? "");
    setTagIds(trade?.tags.map(x => x.id) ?? []);
    setRating(trade?.setupRating ?? null);
  }, [trade?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Les fenêtres restent montées après suppression (le trade disparaît, le panneau se ferme).
  if (!trade) return editing.dialogs;
  const account = trade.accountId ? accountsById.get(trade.accountId) : undefined;
  const strategy = strategies.find(s => trade.strategyIds.includes(s.id));
  const held = new Date(trade.closedAt).getTime() - new Date(trade.openedAt).getTime();
  const points = trade.exit !== null ? (trade.exit - trade.entry) * (trade.side === "long" ? 1 : -1) : null;

  const save = async (patch: { notes?: string; tagIds?: string[]; setupRating?: number | null }) => {
    try {
      await updateTrade({ id: trade.id, ...patch });
      refetchAll();
    } catch (e: any) {
      toast({ title: e?.message ?? String(e), variant: "destructive" });
    }
  };
  const toggleTag = (id: string) => {
    const next = tagIds.includes(id) ? tagIds.filter(x => x !== id) : [...tagIds, id];
    setTagIds(next);
    void save({ tagIds: next });
  };
  const rate = (n: number) => {
    const next = rating === n ? null : n;
    setRating(next);
    void save({ setupRating: next });
  };

  return (
    <>
    {editing.dialogs}
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-[440px] bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200" role="dialog" aria-label={trade.symbol}>
        <header className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-xl font-bold">{trade.symbol}</span>
              <SideTag side={trade.side} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">{fmt.dateLong(trade.openedAt)} · {fmt.time(trade.openedAt)}</p>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground" aria-label={t.common.close}><X size={18} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div>
            <div className="text-xs text-muted-foreground">{t.trade.netResult}</div>
            {trade.closed ? <Pnl value={net(trade)} decimals className="text-4xl font-semibold tracking-tight" /> : <div className="text-2xl font-semibold text-muted-foreground">{t.trade.open}</div>}
            <div className="text-xs text-muted-foreground/70 mt-1 font-mono">{t.trade.grossAndFees(fmt.money(trade.pnl, { decimals: true }), fmt.money(trade.fees, { decimals: true }))}</div>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Field label={t.trade.entry}><span className="font-mono">{fmt.price(trade.entry)}</span></Field>
            <Field label={t.trade.exit}><span className="font-mono">{trade.exit !== null ? fmt.price(trade.exit) : "-"}</span></Field>
            <Field label={t.trade.quantity}><span className="font-mono">{t.trade.contracts(trade.qty)}</span></Field>
            <Field label={t.trade.points}>{points !== null ? <span className={`font-mono ${points >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}>{points >= 0 ? "+" : "−"}{fmt.price(Math.abs(points))}</span> : "-"}</Field>
            <Field label={t.trade.duration}>{fmt.duration(Math.max(0, held))}</Field>
            <Field label={t.trade.exitAt}><span className="font-mono">{fmt.time(trade.closedAt)}</span></Field>
          </dl>

          {account && (
            <div className="rounded-xl border border-border p-3 flex items-center gap-3">
              <FirmMark firm={account.firm} />
              <div className="min-w-0 flex-1">
                <Link to={`/account/${account.id}`} onClick={onClose} className="text-sm font-medium hover:text-primary">{account.name}</Link>
              </div>
            </div>
          )}

          <div>
            <div className={labelCls}>{t.trade.strategy}</div>
            <div className="flex items-center gap-2 text-sm">
              {strategy ? <><span className="w-2 h-2 rounded-full" style={{ background: strategy.color }} />{strategy.name}</> : <span className="text-muted-foreground">{t.trade.noStrategy}</span>}
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-1">{t.trade.strategyHint}</p>
          </div>

          <div>
            <div className={labelCls}>{t.trade.rating}</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button type="button" key={n} onClick={() => rate(n)} aria-label={`${n}/5`} className="p-0.5">
                  <Star size={20} className={rating !== null && n <= rating ? "fill-warning text-warning" : "text-muted-foreground/40"} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className={labelCls}>{t.trade.tags}</div>
            {tags.length ? (
              <div className="flex flex-wrap gap-1.5">
                {tags.map(tag => (
                  <button type="button" key={tag.id} onClick={() => toggleTag(tag.id)}
                    className={`h-7 px-2.5 rounded-full text-xs border transition-colors ${tagIds.includes(tag.id) ? "border-primary/50 bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    {tag.name}
                  </button>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">{t.trade.noTags}</p>}
          </div>

          <div>
            <label className={labelCls} htmlFor="lite-note">{t.trade.note}</label>
            <textarea
              id="lite-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              onBlur={() => { if (note !== trade.note) void save({ notes: note }); }}
              rows={4}
              placeholder={t.trade.notePlaceholder}
              className="w-full rounded-xl bg-muted border border-border px-3 py-2.5 text-sm resize-none leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <Link to={`/trade/${trade.id}`} onClick={onClose} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
            <ExternalLink size={14} /> {t.trade.fullDetail}
          </Link>
        </div>
        <footer className="flex items-center justify-between gap-2 px-6 py-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={() => editing.openEdit((fullTrade as any) ?? trade.raw)}>
            <Pencil className="h-4 w-4 mr-1.5" />{t.trade.edit}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => { editing.openDelete(trade.raw); onClose(); }}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />{t.trade.delete}
          </Button>
        </footer>
      </aside>
    </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-medium">{children}</dd>
    </div>
  );
}
